import React, { useState, useMemo, useEffect } from 'react';
import Modal, {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle,
} from '../../../components/bootstrap/Modal';
import Button from '../../../components/bootstrap/Button';
import Input from '../../../components/bootstrap/forms/Input';
import Select from '../../../components/bootstrap/forms/Select';
import Card from '../../../components/bootstrap/Card';
import Icon from '../../../components/icon/Icon';
import Badge from '../../../components/bootstrap/Badge';
import Spinner from '../../../components/bootstrap/Spinner';
import { getAPI } from '../../../apis/backend/apiNode';

const DemandePrixSelectionModal = ({ 
  isOpen, 
  setIsOpen, 
  onSelect, 
  selectedProducts = [] 
}) => {
	const [searchTerm, setSearchTerm] = useState('');
	const [demandesPrix, setDemandesPrix] = useState([]);
	const [loading, setLoading] = useState(false);
	const [selectedDemande, setSelectedDemande] = useState('');
	const [selectedProduit, setSelectedProduit] = useState('');
	const [selectedReponse, setSelectedReponse] = useState('');

	// Charger les demandes de prix avec réponses sélectionnées
	useEffect(() => {
		if (isOpen) {
			loadDemandesPrix();
		}
	}, [isOpen]);

	const loadDemandesPrix = async () => {
		setLoading(true);
		try {
			const result = await getAPI('achat', '/demandes-prix/reponses-selectionnees');
			if (result?.success) {
				setDemandesPrix(result.data || []);
			}
		} catch (err) {
			console.error('Erreur chargement demandes prix:', err);
		} finally {
			setLoading(false);
		}
	};

	// Filtrer les demandes de prix
	const filteredDemandes = useMemo(() => {
		let filtered = demandesPrix;

		if (searchTerm.trim()) {
			filtered = filtered.filter(demande =>
				demande.reference?.toLowerCase().includes(searchTerm.toLowerCase()) ||
				demande.fournisseurId?.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
				demande.lignes?.some(ligne => 
					ligne.description?.toLowerCase().includes(searchTerm.toLowerCase())
				)
			);
		}

		return filtered;
	}, [demandesPrix, searchTerm]);

	// Obtenir les produits d'une demande sélectionnée
	const produitsDemande = useMemo(() => {
		if (!selectedDemande) return [];
		
		const demande = demandesPrix.find(d => d._id === selectedDemande);
		if (!demande) return [];

		return demande.lignesAvecReponses || demande.lignes?.filter(ligne => 
			demande.reponses?.some(rep => 
				rep.ligneId.toString() === ligne._id.toString() && rep.estSelectionnee
			)
		) || [];
	}, [selectedDemande, demandesPrix]);

	// Obtenir les réponses pour un produit sélectionné
	const reponsesProduit = useMemo(() => {
		if (!selectedDemande || !selectedProduit) return [];
		
		const demande = demandesPrix.find(d => d._id === selectedDemande);
		if (!demande) return [];

		return demande.reponses?.filter(rep => 
			rep.ligneId.toString() === selectedProduit && rep.estSelectionnee
		) || [];
	}, [selectedDemande, selectedProduit, demandesPrix]);

	// Réinitialiser les sélections
	const resetSelections = () => {
		setSelectedDemande('');
		setSelectedProduit('');
		setSelectedReponse('');
	};

	// Gérer la sélection
	const handleSelect = () => {
		if (!selectedDemande || !selectedProduit || !selectedReponse) {
			alert('Veuillez sélectionner une demande, un produit et une réponse');
			return;
		}

		const demande = demandesPrix.find(d => d._id === selectedDemande);
		const produit = produitsDemande.find(p => p._id === selectedProduit);
		const reponse = reponsesProduit.find(r => r._id === selectedReponse);

		if (demande && produit && reponse) {
			onSelect({
				demandePrixId: demande._id,
				reponseId: reponse._id,
				produitInfo: {
					produitId: produit.produitId?._id || produit.produitId,
					description: produit.description,
					quantite: produit.quantite,
					unite: produit.unite,
					prix: reponse.prixPropose,
					fournisseur: demande.fournisseurId?.nom,
					delaiLivraison: reponse.delaiLivraison
				}
			});
			
			resetSelections();
			setIsOpen(false);
		}
	};

	// Vérifier si un produit est déjà sélectionné
	const isProduitAlreadySelected = (demandeId, reponseId) => {
		return selectedProducts.some(prod => 
			prod.demandePrixId === demandeId && prod.reponseId === reponseId
		);
	};

	return (
		<Modal isOpen={isOpen} setIsOpen={setIsOpen} size="xl" className="">
			<ModalHeader setIsOpen={setIsOpen}>
				<ModalTitle>
					<Icon icon="PriceCheck" className="me-2" />
					Sélectionner depuis Demandes de Prix
				</ModalTitle>
			</ModalHeader>
			
			<ModalBody>
				{/* Barre de recherche */}
				<div className="row g-3 mb-4">
					<div className="col-md-8">
						<Input
							type="text"
							placeholder="Rechercher par référence, fournisseur, produit..."
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
						/>
					</div>
				</div>

				{loading ? (
					<div className="text-center py-4">
						<Spinner color="primary" size="3x" />
						<div className="text-muted mt-3">Chargement des demandes de prix...</div>
					</div>
				) : (
					<div className="row">
						{/* Liste des demandes de prix */}
						<div className="col-md-4 border-end">
							<h6 className="fw-bold mb-3">Demandes de Prix</h6>
							<div style={{ maxHeight: '400px', overflowY: 'auto' }}>
								{filteredDemandes.length === 0 ? (
									<div className="text-center py-4">
										<Icon icon="SearchOff" size="2x" className="text-muted mb-2" />
										<p className="text-muted">Aucune demande de prix trouvée</p>
									</div>
								) : (
									filteredDemandes.map((demande) => (
										<Card
											key={demande._id}
											className={`cursor-pointer mb-2 ${
												selectedDemande === demande._id ? 'border-primary' : ''
											}`}
											onClick={() => {
												setSelectedDemande(demande._id);
												setSelectedProduit('');
												setSelectedReponse('');
											}}
										>
											<div className="p-3">
												<h6 className="fw-bold mb-1">{demande.reference}</h6>
												<Badge color="info" isLight size="sm" className="mb-2">
													{demande.fournisseurId?.nom}
												</Badge>
												<div className="small text-muted">
													{demande.lignesAvecReponses?.length || 0} produits sélectionnés
												</div>
												<Badge 
													color={
														demande.statut === 'complètement_utilisée' ? 'success' :
														demande.statut === 'partiellement_utilisée' ? 'warning' : 'primary'
													} 
													isLight 
													size="sm"
												>
													{demande.statut}
												</Badge>
											</div>
										</Card>
									))
								)}
							</div>
						</div>

						{/* Produits de la demande sélectionnée */}
						<div className="col-md-4 border-end">
							<h6 className="fw-bold mb-3">Produits Sélectionnés</h6>
							{!selectedDemande ? (
								<div className="text-center py-4">
									<Icon icon="Info" size="2x" className="text-muted mb-2" />
									<p className="text-muted">Sélectionnez une demande de prix</p>
								</div>
							) : produitsDemande.length === 0 ? (
								<div className="text-center py-4">
									<Icon icon="Warning" size="2x" className="text-muted mb-2" />
									<p className="text-muted">Aucun produit sélectionné dans cette demande</p>
								</div>
							) : (
								<div style={{ maxHeight: '400px', overflowY: 'auto' }}>
									{produitsDemande.map((produit) => {
										const reponse = demandesPrix
											.find(d => d._id === selectedDemande)
											?.reponses?.find(rep => 
												rep.ligneId.toString() === produit._id.toString() && rep.estSelectionnee
											);
										
										const isSelected = isProduitAlreadySelected(selectedDemande, reponse?._id);

										return (
											<Card
												key={produit._id}
												className={`cursor-pointer mb-2 ${
													selectedProduit === produit._id ? 'border-primary' : ''
												} ${isSelected ? 'border-success' : ''}`}
												onClick={() => {
													if (!isSelected) {
														setSelectedProduit(produit._id);
														setSelectedReponse(reponse?._id || '');
													}
												}}
											>
												<div className="p-3">
													<div className="d-flex justify-content-between align-items-start">
														<div>
															<h6 className="fw-bold mb-1">{produit.description}</h6>
															<div className="small text-muted">
																Qté: {produit.quantite} {produit.unite}
															</div>
														</div>
														{isSelected && (
															<Badge color="success" isLight>
																Déjà sélectionné
															</Badge>
														)}
													</div>
													{reponse && (
														<div className="mt-2">
															<Badge color="success" className="me-2">
																{reponse.prixPropose} €
															</Badge>
															{reponse.delaiLivraison && (
																<Badge color="info" isLight>
																	{reponse.delaiLivraison}
																</Badge>
															)}
														</div>
													)}
												</div>
											</Card>
										);
									})}
								</div>
							)}
						</div>

						{/* Détails du produit sélectionné */}
						<div className="col-md-4">
							<h6 className="fw-bold mb-3">Détails de Sélection</h6>
							{!selectedProduit ? (
								<div className="text-center py-4">
									<Icon icon="Info" size="2x" className="text-muted mb-2" />
									<p className="text-muted">Sélectionnez un produit</p>
								</div>
							) : (
								<div className="bg-light p-3 rounded">
									{(() => {
										const demande = demandesPrix.find(d => d._id === selectedDemande);
										const produit = produitsDemande.find(p => p._id === selectedProduit);
										const reponse = reponsesProduit[0]; // Une seule réponse sélectionnée par produit
										
										if (!demande || !produit || !reponse) return null;

										return (
											<>
												<h6 className="fw-bold">{produit.description}</h6>
												<div className="small">
													<div className="mb-2">
														<strong>Fournisseur:</strong> {demande.fournisseurId?.nom}
													</div>
													<div className="mb-2">
														<strong>Quantité:</strong> {produit.quantite} {produit.unite}
													</div>
													<div className="mb-2">
														<strong>Prix unitaire:</strong> {reponse.prixPropose} € HT
													</div>
													<div className="mb-2">
														<strong>Total:</strong> {(produit.quantite * reponse.prixPropose).toFixed(2)} € HT
													</div>
													{reponse.delaiLivraison && (
														<div className="mb-2">
															<strong>Délai:</strong> {reponse.delaiLivraison}
														</div>
													)}
													{reponse.conditions && (
														<div>
															<strong>Conditions:</strong> {reponse.conditions}
														</div>
													)}
												</div>
											</>
										);
									})()}
								</div>
							)}
						</div>
					</div>
				)}
			</ModalBody>

			<ModalFooter>
				<Button color="secondary" onClick={() => {
					resetSelections();
					setIsOpen(false);
				}}>
					Annuler
				</Button>
				<Button
					color="primary"
					onClick={handleSelect}
					disabled={!selectedDemande || !selectedProduit || !selectedReponse}
				>
					<Icon icon="Check" className="me-2" />
					Ajouter au Devis
				</Button>
			</ModalFooter>
		</Modal>
	);
};

export default DemandePrixSelectionModal;